import { createSlice } from "@reduxjs/toolkit";
import {addErrorFields, setErrors, setLockedFieldsIgnoringDisabled} from "../configuratorStateSlice";
import {
    clearOptionalAttributes,
    clearPerModuleSelections, setAttributeSelectionsFromSelections
} from "../product/productSelectionsSlice";

export const disabledFieldsSlice = createSlice({
    name: 'disabledFields',
    initialState: {
        loading: 'idle',
        disabledFields: [],
        disabledModules: [],
        disabledElementFields: [],
        disabledElementModules: [],
        sideDisabledFields: [],
        sideModuleDisabledFields: [],
        messages: []
    },
    reducers: {
        disabledFieldsLoading(state) {
            if(state.loading === 'idle'){
                state.loading = 'pending';
            }
        },
        disabledFieldsSet(state, action) {
            if(state.loading === 'pending') {
                state.loading = 'idle';
                state.disabledFields = action.payload;
            }
        },
        disabledModulesSet(state,action){
            if(state.loading === 'pending'){
                state.loading = 'idle';
                state.disabledModules = action.payload;
            }
        },
        disabledElementFieldsSet(state, action) {
            if(state.loading === 'pending') {
                state.loading = 'idle';
                state.disabledElementFields = action.payload;
            }
        },
        disabledElementModulesSet(state, action) {
            if(state.loading === 'pending') {
                state.loading = 'idle';
                state.disabledElementModules = action.payload;
            }
        },
        sideDisabledFieldsSet(state, action){
            if(state.loading === 'pending') {
                state.loading = 'idle';
                state.sideDisabledFields = action.payload;
            }
        },
        disabledSideModulesSet(state, action){
            if(state.loading === 'pending'){
                state.loading = 'idle';
                state.sideModuleDisabledFields = action.payload;
            }
        }

    }
});

export const {disabledFieldsLoading, disabledFieldsSet, sideDisabledFieldsSet, disabledModulesSet, disabledSideModulesSet, disabledElementFieldsSet, disabledElementModulesSet} = disabledFieldsSlice.actions;


/*
    This method will update and disable fields that match an exception. The main
    premise behind exceptions is that when all but one have a value matching a selection
    the remaining exception will have those selections disabled. For example if an exceptions
    consist of:
    [
        {attribute_id: 1, selection: A},
        {attribute_id: 2, selection: B},
        {attribute_id: 3, selection: C}
    ]
     and current selections are:
     [
        {attribute_id: 1, A},
        {attribute_id: 2, B},
     ]

     selection C will be disabled on attribute 3.
 */
export const updateDisabledFields = () => (dispatch, getState) => {

    dispatch(disabledFieldsLoading());
    const state = getState();
    const product = state.productDataReducer;
    const exceptions = state.skuExceptionsReducer.exceptions;
    const isPattern = state.configuratorStateReducer.isPattern;
    const patternSideSelections = product.side_attribute_selections;
    // prepare our disabled fields. Each time this is ran we clear disabled fields to have a fresh start
    // this simplifies the logic
    const disabledFields = product.product_attributes.map((attribute) => ({id: attribute.id, field: attribute.name, disabledSelections: [], messages: []  }));
    const attrSelections = product.attribute_selections.map((attr_s) => ({ id: attr_s.id, selection: attr_s.selection, per_module: attr_s.per_module, module_catalog_key: attr_s.module_catalog_key }));
    const modules = state.productSelectionsReducer.perModuleSelections;

    const updatedDisabledFields = getDisabledFieldsFromExceptions(product, exceptions, disabledFields, attrSelections, modules, isPattern, patternSideSelections, false, dispatch);

    dispatch(disabledFieldsSet(updatedDisabledFields));
    dispatch(updateDisabledModules());
};

export const updateDisabledElementFields = () => (dispatch, getState) => {
    if(!getState().configuratorStateReducer.hasElements) {
        return;
    }
    dispatch(disabledFieldsLoading());
    
    const elements = getState().productElementsReducer.elements_with_selections;
    const updatedDisabledFields = [];
    for(let element of elements) {
        const disabledFields = element.product_attributes.map((attribute) => ({id: attribute.id, field: attribute.name, disabledSelections: [], messages: []  }));
        const attrSelections = element.attribute_selections.map((attr_s) => ({ id: attr_s.id, selection: attr_s.selection, per_module: attr_s.per_module, module_catalog_key: attr_s.module_catalog_key }));
        const elementDisabledFields = getDisabledFieldsFromExceptions(element, element.sku_exceptions, disabledFields, attrSelections, element.per_module_selections, false, [], true, dispatch);
        updatedDisabledFields.push(elementDisabledFields);
    }

    dispatch(disabledElementFieldsSet(updatedDisabledFields));
}

export const validSelection = (selection, attribute,  element) => (dispatch, getState) => {
    const disabledFields = element.product_attributes.map((attribute) => ({id: attribute.id, field: attribute.name, disabledSelections: [], messages: []  }));
    const attrSelections = element.attribute_selections.map((attr_s) => ({ id: attr_s.id, selection: attr_s.selection, per_module: attr_s.per_module, module_catalog_key: attr_s.module_catalog_key }));
    const elementDisabledFields = getDisabledFieldsFromExceptions(element, element.sku_exceptions, disabledFields, attrSelections, element.per_module_selections, false, [], true, dispatch);

    const matchingField = elementDisabledFields.find(edf => edf.id == attribute.id);
    return !matchingField.disabledSelections.includes(selection)
}

export const getDisabledFieldsFromExceptions = (product, exceptions, disabledFields, attrSelections, modules, isPattern, patternSideSelections, isElement , dispatch) => {
    const updatedDisabledFields = Object.assign([], disabledFields);
    exceptions.forEach((exc) => {
        // exceptions between ONLY per module attributes are handled elsewhere, so we skip
        // same with inclusive attributes
        if(exc.within_module){
            return;
        }

        let exceptionResults;
        let exceptionFieldsAsArray = Object.entries(exc.exceptionFields)
        // View all fields in the current exception
        if(exc.inclusion){
            exceptionResults = handleInclusivePairs(exc, attrSelections, product.product_attributes, modules);
        } else {
            exceptionResults = handleExclusivePairs(exceptionFieldsAsArray, attrSelections, isPattern, patternSideSelections);
        }

        // Here we store fields with selections that DO NOT match a current exception.
        // If this arrays length is 1 then that exception will need to be toggled.
        let exceptionsWithoutMatchingField = exceptionResults.fields;
        // Since selections are added to attribute_selection as they are displayed
        // we keep track of how many are empty to skip false positives
        let emptyCount = exceptionResults.emptyCount;
        // if there are more than one empty than we dont want to disable anything.
        // If there is only one empty then this field will have disabled fields so we proceed
        if(emptyCount > 1 && !exc.inclusion){
            return;
        }

        const exceptionErrors = exceptionHasErrors(exc, attrSelections, product.product_attributes, modules);
        if(exceptionErrors.error && !isElement){
            dispatch(setErrors(exceptionErrors.message));
            dispatch({type: 'ADD_NOTICE', notice_type: 'catalog_number', message: 'Invalid Catalog Number'})
            dispatch(addErrorFields(exceptionErrors.field));
            dispatch(clearOptionalAttributes());
            dispatch(clearPerModuleSelections());
            dispatch(setAttributeSelectionsFromSelections());
        }

        // if there is only 1 exception without a matching field or there is only 1 exception
        // this exception is active and should disabled selections. Inclusion was added after
        // and can have multiple to shut off.
        if(exceptionsWithoutMatchingField.length == 1 || exc.inclusion){
            exceptionsWithoutMatchingField.forEach(excField => {
                const matchedId = excField[0];
                const disabledSelections = excField[1];

                const disabledField = updatedDisabledFields.find((df) => df.id == matchedId)

                // this should always be true as we build them as we get exceptions,
                // but we check anyways to avoid errors
                if(disabledField) {
                    let currentDisabled = Object.assign([], disabledField.disabledSelections);
                    if (Array.isArray(disabledSelections)) {
                        currentDisabled = currentDisabled.concat(disabledSelections);
                    } else {
                        currentDisabled = currentDisabled.push(disabledSelections);
                    }
                    updatedDisabledFields.some((df, index) => {
                        if (df.id == disabledField.id) {
                            updatedDisabledFields[index] = Object.assign({}, df, {
                                disabledSelections: arrayUnique(currentDisabled.concat(df.disabledSelections)),
                                messages: arrayUnique(df.messages.concat(exc.description))
                            });
                            return true
                        }
                    });
                }
            })
        }
    });

    return updatedDisabledFields;
}

/**
 * @description Compares exception fields with selections and returns an error if required. Exclusive exceptions
 * return an error when ALL fields match, inclusive when the target has a matching selection while controls dont.
 * @param exc
 * @param fields
 * @returns {{error: boolean, message: string, fields: *[]}}
 */
export const exceptionHasErrors = (exc, selections, attributes, modules) => {
    let error;
    if(exc.inclusion){
        error = inclusiveHasErrors(exc, selections, attributes, modules);
    } else {
        error = exclusiveHasErrors(exc.exceptionFields, selections);
    }
    return error;
}

/**
 * @description Compares exception fields with selections and returns an error if required. Exclusive exceptions
 * return an error when ALL fields match
 * @param exc
 * @param fields
 * @returns {{error: boolean, message: string, fields: *[]}}
 */
export const exclusiveHasErrors = (exceptionFields, selections) => {
    let error = {error: false, message: ''};
    let matches = 0;
    const fields = Object.entries(exceptionFields);
    fields.forEach(field => {
        const fieldId = field[0];
        const value = field[1];
        let matching = selections.find(sel => sel.id == fieldId && (value.includes(sel.selection) || (value.includes(sel.module_catalog_key) && sel.selection)));
        if(matching){
            matches++;
        }
    })
    if(fields.length == matches){
        // get the last field of the exception;
        const fieldId =  fields[fields.length - 1][0];
        error = {error: true, message:'Invalid Catalog Number', field: fieldId};
    }
    return error;
}

/**
 * @description Compares exception fields with selections and returns an error if required. Exclusive exceptions
 * return an error when ALL fields match
 * @param exc
 * @param fields
 * @returns {{error: boolean, message: string, fields: *[]}|null}
 */
export const inclusiveHasErrors = (exception, selections, attributes, modules) => {
    let error = {error: false, message: ''};
    const exceptionFieldIds = Object.entries(exception.exceptionFields).map(ef => ef[0]);
    const controlledFields = JSON.parse(exception.inclusion_controlled_attributes);
    const controlFields = exceptionFieldIds.filter(efId => !controlledFields.includes(efId));

    let controlledFieldSelected = false;
    controlledFields.forEach(cf => {
        let matchingField = selections.find(as => as.id == cf);
        const values = exception.exceptionFields[cf];
        if(matchingField && matchingField.per_module){
            // handle module
            const modSelections = modules.map(mod => mod.selections);
            if(modSelections.includes(values[0]) ){
                controlledFieldSelected = true;
                return;
            }
        } else {
            // handle non module
            if(values && matchingField && values.includes(matchingField.selection)){
                controlledFieldSelected = true;
                return;
            }
        }
    })

    if(!controlledFieldSelected){
        return error;
    }
    // now check controlled fields match
    let controlsMatching = true;
    controlFields.forEach(fieldId => {
        let matchingField = selections.find(as => as.id == fieldId);
        if(!matchingField) {
            matchingField = attributes.find(pa => pa.id == fieldId);
            const moduleFields = ['module_position', 'module_length'];
            if(matchingField && !moduleFields.includes(matchingField.internal_name)){
                controlsMatching = false;
                return;
            }
        }

        const values = exception.exceptionFields[fieldId];

        // module values are stored elsewhere, so apply different logic
        if(matchingField && matchingField.per_module){
            const modSelections = []
            // here we put any attributes we may need into an array, later
            // we check if the array does not contain the sku exception required
            // value
            modules.forEach(mod => {
                modSelections.concat(mod.selections)
                modSelections.push(mod.length)
                modSelections.push(mod.position)
            })
            // per module exceptions only make use of a single value, module_catalog_key
            // so we can safely pass values[0] below
            if(!modSelections.includes(values[0]) ){
                controlsMatching = false;
            }

        } else {
            // handle non module
            if(values && matchingField && !values.includes(matchingField.selection)){
                controlsMatching = false;
            }
        }

    })

    if(!controlsMatching){
        // get the last field of the exception;
        const fieldId =  controlledFields[controlledFields.length - 1];
        error = {error: true, message:'Invalid Catalog Number', field: fieldId};
    }

    return error;
}

/**
 * @description Goes through an array of exceptionFields and checks if each field has a selection that matches. Fields
 * without a selection matching the exception are counted and sent back. Note that empty count returns the number of
 * fields without any selection at all, while fields will contain both fields without selection and fields that have a
 * selection that does not match the exceptions list of selections.
 * @param exceptionFields {array} Current Exception Fields
 * @param attrSelections {array} Current Required and Optional Selections
 * @param isPattern {bool}
 * @param patternSideSelections {array} Current side selections
 * @returns {{emptyCount: number, fields: *[]}} An Object mapping total empty count and any exception fields without
 * a matching selection
 */
export const handleExclusivePairs = (exceptionFields, attrSelections, isPattern, patternSideSelections) => {
   const exceptionsWithoutMatchingField = [];
   let emptyCount = 0
    // exceptions with only attributes only disable the last step of the exception.
    // Here we look to see if it is an attribute only exception.
    const exceptionIds = exceptionFields.map(exc => exc[0]);
    const attributeOnlyException = attrSelections.filter(sel => (exceptionIds.includes(sel.id.toString()) && !sel.optional && !sel.per_module)).length == exceptionFields.length
    if(attributeOnlyException){
            let exceptionField;
            const lastIndex = exceptionIds.length - 1;
            let activeCount = 0;
            for(let x = 0; x < exceptionIds.length; x++) {
                const currentKey = exceptionIds[x];
                const matchingCurrentSelection = attrSelections.find((object, index) => object.id == currentKey && index < attrSelections.length);
                const currentSelection = matchingCurrentSelection ?  matchingCurrentSelection.selection : null;
                // go through all keys, if the key current is active, increment the active count;
                exceptionField = exceptionFields.find(field => field[0] == currentKey);
                if (matchingCurrentSelection && exceptionField[1].includes(currentSelection) && x != lastIndex ) {
                    activeCount++;
                }
            }
            // if the active count is 1 less than total, then we need to activate the last item
            if(activeCount == lastIndex) {
                exceptionsWithoutMatchingField.push(exceptionField);
                emptyCount = 1;
            }
    }else {
        exceptionFields.forEach((field) => {
            const selID = field[0];
            const exceptionSelections = field[1];

            // Look for a field in attribute selections that matches the current field
            const matchingField = attrSelections.find((sel) => sel.id == selID);
            if (matchingField) {

                if (matchingField.per_module && exceptionSelections.includes(matchingField.module_catalog_key)) {
                    // per module fields MAY be using just module catalog key instead of value
                    // so we handle that here. This will check if the exception triggers contain
                    // the module catalog key, and if it does then we want to check if there is
                    // any selection.
                    if (!matchingField.selection) {
                        exceptionsWithoutMatchingField.push(field);
                    }
                } else {
                    // Add the field to our array if its selection list does not contain current selection
                    if (!exceptionSelections.includes(matchingField.selection)) {
                        exceptionsWithoutMatchingField.push(field);
                    }
                    // if there is no selection it is empty, increment our counter
                    if (!matchingField.selection) {
                        emptyCount++;
                    }
                }

            } else {
                // Pattern sides may disable a field, look at them here
                if (isPattern) {
                    const flattenedSideSelections = Object.entries(patternSideSelections).flatMap(s => s[1].product_attributes)
                    const matchingPatternSelection = flattenedSideSelections.find(ps => ps.id == selID);
                    if (!matchingPatternSelection) {
                        exceptionsWithoutMatchingField.push(field);
                        emptyCount++
                    } else if (!exceptionSelections.includes(matchingPatternSelection.selection)) {
                        exceptionsWithoutMatchingField.push(field);
                    }
                } else {
                    // if theres no matching field we can assume it is empty
                    exceptionsWithoutMatchingField.push(field);
                    emptyCount++;
                }
            }
        });
    }

    return ({
        fields: exceptionsWithoutMatchingField,
        emptyCount: emptyCount
    })
}

export const handleInclusivePairs = (exception, attrSelections, productAttributes, modules) => {
    const selectionsToDisable = [];
    let emptyCount = 0

    const exceptionFieldIds = Object.entries(exception.exceptionFields).map(ef => ef[0]);
    const controlledFields = JSON.parse(exception.inclusion_controlled_attributes);
    const controlFields = exceptionFieldIds.filter(efId => !controlledFields.includes(efId));
    // default to controls matching, we will disable after looking through contorl fields if
    // values are missing or do not match.
    let controlsMatching =  true;

    controlFields.forEach(fieldId => {
        let matchingField = attrSelections.find(as => as.id == fieldId);
        if(!matchingField) {
            // module length and position are not stored in attrSelections
            // for this case we look into product_attributes. We only use attributes
            // if the attribute is module position or length - this is because all other
            // attributes will be inside attrSelections if they are not blank; blank ones are handle
            // the same way so we handle the logic here as well.
            matchingField = productAttributes.find(pa => pa.id == fieldId);
            const moduleFields = ['module_position', 'module_length'];
            if(matchingField && !moduleFields.includes(matchingField.internal_name)){
                controlsMatching = false;
                emptyCount++;
                return;
            }
        }

        const values = exception.exceptionFields[fieldId];

        // module values are stored elsewhere, so apply different logic
        if(matchingField && matchingField.per_module){
            const modSelections = []
            // here we put any attributes we may need into an array, later
            // we check if the array does not contain the sku exception required
            // value
            modules.forEach(mod => {
                modSelections.concat(mod.selections)
                modSelections.push(mod.length)
                modSelections.push(mod.position)
            })
            // per module exceptions only make use of a single value, module_catalog_key
            // so we can safely pass values[0] below
            if(!modSelections.includes(values[0]) ){
                controlsMatching = false;
            }

        } else {
            // handle non module
            if(values && matchingField && !values.includes(matchingField.selection)){
                controlsMatching = false;
            }
        }

    })

    if(!controlsMatching){
        Object.entries(exception.exceptionFields).forEach(field => {
            const id = field[0];
            if(controlledFields.includes(id)) {
                selectionsToDisable.push(field);
            }
        })

    }

    return ({
        fields: selectionsToDisable,
        emptyCount: emptyCount
    })
}

export const updateDisabledModules = () => (dispatch, getState) => {
    dispatch(disabledFieldsLoading());
    // these exceptions are at the module level, only effect a SINGLE module,
    // ie preventing Alt in a module where BP is selected. We use Module Length and
    // Module Position read only fields to allow exceptions for module lengths

    const state = getState();
    const product = state.productDataReducer;
    const perModuleSelections = state.productSelectionsReducer.perModuleSelections;
    const exceptions = state.skuExceptionsReducer.exceptions
    const disabledModules = perModuleSelections.map(attr => ({id: attr.module_id, field: attr.position + "-" + attr.length, disabledSelections: [], messages: []  }))

    // hardcoded for now, values are passed from back end for exceptions only
    exceptions.forEach(exc => {
        if(!exc.within_module){
            return;
        }

        perModuleSelections.forEach(mod => {
            let unselectedModuleOptions;

            if(exc.inclusion){
                unselectedModuleOptions = handleInclusiveModules(exc, mod);
            } else {
                unselectedModuleOptions = handleExclusiveModules(exc, mod, product);
            }

            // inclusive may have multiple to disable
            if(unselectedModuleOptions.length == 1 || exc.inclusion) {
                unselectedModuleOptions.forEach(unselected => {
                    const disabledModule = disabledModules.find((df) => df.id == mod.module_id)

                    // this should always be true as we build them as we get exceptions,
                    // but we check anyways to avoid errors
                    if (disabledModule) {
                        let currentDisabled = Object.assign([], disabledModule.disabledSelections);
                        currentDisabled = currentDisabled.concat(unselected[1]);

                        disabledModules.some((df, index) => {
                            if (df.id == disabledModule.id) {
                                disabledModules[index] = Object.assign({}, df, {
                                    disabledSelections: arrayUnique(currentDisabled.concat(df.disabledSelections)),
                                    messages: arrayUnique(df.messages.concat(exc.description))
                                });
                                return true
                            }
                        });
                    }
                })
            }
        })

    })
    dispatch(disabledModulesSet(disabledModules));
}

const handleInclusiveModules = (exc, mod) => {
    const selectionsToDisable = [];

    const exceptionFieldIds = Object.entries(exc.exceptionFields).map(ef => ef[0]);
    const controlledFields = JSON.parse(exc.inclusion_controlled_attributes);
    const controllerFields = exceptionFieldIds.filter(efID => !controlledFields.includes(efID));
    let controlsMatching = true;

    // if the module doesnt match all selections, we disable controlledFields
    const moduleSelections = mod.selections;
    controllerFields.forEach(cf => {
        if(!moduleSelections.includes(exc.exceptionFields[cf])){
            controlsMatching = false;
        }
    })

    if(!controlsMatching){
        // go through all exceptions and disable any that need to be
        Object.entries(exc.exceptionFields).forEach(ef => {
            if(controlledFields.includes(ef[0])){
                selectionsToDisable.push(ef);
            }
        })
    }

    return selectionsToDisable;
}

const handleExclusiveModules = (exc, mod, product, isPattern = false, sideKey = null) => {
    const unselectedModuleOptions = [];
    Object.entries(exc.exceptionFields).forEach(fieldSel => {
        let attribute;
        if( !isPattern) {
            attribute = product.product_attributes.find(pa => pa.id == fieldSel[0]);
        } else {
            attribute = product.side_attributes[sideKey].product_attributes.find(pa => pa.id == fieldSel[0]);
        }
        // handles read only attributes, since naming is weird we hard code it above
        if(attribute && attribute.read_only && attribute.per_module){
            if(attribute.internal_name == "module_length"){
                if(!fieldSel[1].includes(mod.length)){
                    unselectedModuleOptions.push(fieldSel);
                }
            } else if(attribute.internal_name == "module_position"){
                if(!fieldSel[1].includes(mod.position)){
                    unselectedModuleOptions.push(fieldSel);
                }
            }
        } else if(!mod.selections.every(sel => fieldSel[1].includes(sel))){
            unselectedModuleOptions.push(fieldSel);
        } else {
            if (mod.selections.length == 0) {
                unselectedModuleOptions.push(fieldSel);
            }
        }
    })
    return unselectedModuleOptions;
}

/**
 * @description Look for attributes with only one available selection. If there are multiple then the
 * first ones id will be passed to remove all disabled selection. This is to prevent a situation where
 * two field exceptions prevent any other selection and cause the system to jam.
 * @returns {(function(*, *): void)|*}
 */
export const checkForLockedAttributes = () => (dispatch, getState) => {
    const state = getState();
    const productAttributes = state.productDataReducer.product_attributes;
    const disabledFields = state.disabledFieldsReducer.disabledFields;
    const currentSelections = state.productDataReducer.attribute_selections;
    let allButOneCount = 0;
    const lockedAttributeIds = [];
    const requiredAttributes = productAttributes.filter((pa) => !pa.optional && !pa.per_module);
    requiredAttributes.forEach((attr) => {
        const disabledField = disabledFields.find(df => df.id == attr.id);
        const disabledFieldCount = disabledField.disabledSelections.length;
        const matching = currentSelections.find(cs =>cs.id == attr.id)
        const hasSelection = matching ? matching.selection:false;
        if(hasSelection && disabledFieldCount >= attr.values.split(",").length - 1){
            allButOneCount++;
            lockedAttributeIds.push(attr.id);
        }
    })

    if(allButOneCount > 1){
        // only add the first id
        dispatch(setLockedFieldsIgnoringDisabled([lockedAttributeIds[0]]))
    } else {
        dispatch(setLockedFieldsIgnoringDisabled([]))
    }
}

function arrayUnique(array) {
    let a = array.concat();
    for(let i=0; i<a.length; ++i) {
        for(let j=i+1; j<a.length; ++j) {
            if(a[i] === a[j])
                a.splice(j--, 1);
        }
    }

    return a;
}

export const setDisabledFields = (product) => dispatch => {
    dispatch(disabledFieldsLoading());
    const mappedAttributes = product.product_attributes.map((attribute) => ({id: attribute.id, field: attribute.name, disabledSelections: [], messages: []  }));
    dispatch(disabledFieldsSet(mappedAttributes));
};

export const setDisabledModules = (modules) => dispatch => {
    dispatch(disabledFieldsLoading());
    const mappedModules = modules.map((module) => ({id: module.module_id, field: module.position + "-" + module.length, disabledSelections: [], messages: []  }));
    dispatch(disabledModulesSet(mappedModules));
};


/**
 * @description Manages exceptions for pattern sides and sets which fields should be disabled. This function
 * will go through all exceptions and selections looking for any of the following:
 *
 * - Exceptions that have all selections but one, and no empty selections
 * - Single Field Exceptions - always disabled
 * - Inclusive exceptions that require other fields to be active in order to be enabled
 */
export const updatePatternSideExceptions = () => (dispatch, getState) => {
    dispatch(disabledFieldsLoading());

    const state = getState();
    const isPattern = state.configuratorStateReducer.isPattern;
    const displaySummary = state.configuratorStateReducer.displayOptionalAndSummary;

    if( !isPattern || !displaySummary){
        return;
    }
    const product =  state.productDataReducer;
    const sideSelections = Object.entries(product.side_attribute_selections);
    const sideAttributes = Object.entries(product.side_attributes);
    const productSelections = product.attribute_selections;
    const exceptions = state.skuExceptionsReducer.exceptions;
    const disabledFields = mapSideAttributesForDisabledFields(sideAttributes);
    const attrSelections = mapSideSelectionsForExceptions(sideSelections);
    const sides = Object.keys(product.side_attributes);

    // Go through each exception in the product
    exceptions.forEach((exc) => {
        let exceptionResults;
        let emptyCount;
        let exceptionsWithoutMatchingField;
        if(exc.inclusion){
            exceptionResults = handlePatternInclusivePairs(exc, attrSelections, sides, productSelections);
        } else {
            exceptionResults = handlePatternExclusivePairs(exc.exceptionFields, attrSelections, productSelections, sides);
        }

        emptyCount = exceptionResults.emptyCount;
        exceptionsWithoutMatchingField = exceptionResults.fields;

        // if there are more than one empty than we dont want to disable anything.
        // If there is only one empty then this field will have disabled fields so we proceed
        sides.forEach(side => {
            if(emptyCount[side] <= 1 || exc.inclusion){

                // if there is only 1 exception without a matching field or there is only 1 exception
                // this exception is active and should disabled selections
                const sideExceptions = exceptionsWithoutMatchingField.filter(exc => exc.side == side)
                if(sideExceptions.length === 1) {
                    const matchedId = sideExceptions[0].field[0];
                    const sideDisabledFields = disabledFields.find(df => Object.keys(df) == side)[side];
                    const disabledSelections = sideExceptions[0].field[1];

                    const disabledField = sideDisabledFields.find((df) => df.id == matchedId && df.side == side)

                    // this should always be true as we build them as we get exceptions,
                    // but we check anyways to avoid errors
                    if (disabledField) {
                        let currentDisabled = Object.assign([], disabledField.disabledSelections);
                        if (Array.isArray(disabledSelections)) {
                            currentDisabled = currentDisabled.concat(disabledSelections);
                        } else {
                            currentDisabled = currentDisabled.push(disabledSelections);
                        }
                        sideDisabledFields.some((df, index) => {
                            if (df.id == disabledField.id) {
                                sideDisabledFields[index] = Object.assign({}, df, {
                                    disabledSelections: arrayUnique(currentDisabled.concat(df.disabledSelections)),
                                    messages: arrayUnique(df.messages.concat(exc.description))
                                });
                                return true
                            }
                        });

                    }
                }
            }
        })

    });

    dispatch(sideDisabledFieldsSet(disabledFields))
    dispatch(updatePatternSideModuleExceptions());
}

/** @description Compares selections with exclusive exceptions. Checks all exception fields to see if they have matching
 *  exceptions and disables a selection where appropriate. Fields without a matching selection are returned
 *  along with the total count of empty fields.
 *  @param {array} exceptionFields Fields the exception is active for
 *  @param {array} attrSelections Current Side Selections
 *  @param {array} productSelections None side selections
 *  @param {array} sides Array of Side Keys (A,B,C)
 *  @returns {{fields: [], emptyCount: []}}  An object where fields has fields to disable and empty count
 *  counts the number of empty exceptions
 */
export const handlePatternExclusivePairs = (exceptionFields, attrSelections, productSelections, sides) => {
    const exceptionsWithoutMatchingField = [];

    let emptyCount = [];
    sides.forEach(side => emptyCount[side] = 0);

    // View all fields in the current exception
    Object.entries(exceptionFields).forEach((field) => {
        const selID = field[0];
        const exceptionSelections = field[1];

        sides.forEach(side => {
            const sideSelections = attrSelections.find(as => Object.keys(as)[0] == side);

            let matchingField = sideSelections ? sideSelections[side].find((sel) => sel.id == selID && sel.side == side):null;
            if (matchingField) {
                if(matchingField.per_module && exceptionSelections.includes(matchingField.module_catalog_key)){
                    if(!matchingField.selection){
                        exceptionsWithoutMatchingField.push({field: field, side: side});
                    }
                } else {
                    // Add the field to our array if its selection list does not contain current selection
                    if (!exceptionSelections.includes(matchingField.selection)) {
                        exceptionsWithoutMatchingField.push({field: field, side: side});
                    }

                    // if there is no selection it is empty, increment our counter
                    if (!matchingField.selection) {
                        emptyCount[side] = emptyCount[side] + 1;
                    }
                }
            } else {
                //check attribute selections for a matching field
                const matchingProductSelection = productSelections.find(sel => sel.id == selID);
                if(!matchingProductSelection || !exceptionSelections.includes(matchingProductSelection.selection)) {
                    exceptionsWithoutMatchingField.push({field: field, side: side});
                    emptyCount[side] = emptyCount[side] + 1;
                }
            }
        })
    });

    return ({
        fields: exceptionsWithoutMatchingField,
        emptyCount: emptyCount
    })
}


/**
 * @description Handle inclusive exceptions for pattern sides. Looks through all control fields
 * and ensures they match selections. If they do not then the controlled field is set as disabled.
 * Looks through all sides for the exception.
 * @param exception
 * @param attrSelections
 * @param sides
 * @returns {{emptyCount: *[], fields: *[]}}
 */
export const handlePatternInclusivePairs = (exception, attrSelections, sides, productSelections) => {
    const selectionsToDisable = [];
    let emptyCount = []
    sides.forEach(side => emptyCount[side] = 0)

    const exceptionFieldIds = Object.entries(exception.exceptionFields).map(ef => ef[0]);
    const controlledFields = JSON.parse(exception.inclusion_controlled_attributes);
    const controlFields = exceptionFieldIds.filter(efId => !controlledFields.includes(efId));
    // default to controls matching, we will disable after looking through contorl fields if
    // values are missing or do not match.
    sides.forEach(side => {
        let controlsMatching =  true;

        controlFields.forEach(fieldId => {
            const sideSelections = attrSelections.find(as => Object.keys(as)[0] == side);
            let matchingField = sideSelections ? sideSelections[side].find((sel) => sel.id == fieldId && sel.side == side):null;
            const values = exception.exceptionFields[fieldId];
            if(matchingField){
                if(values && matchingField && !values.includes(matchingField.selection)){
                    controlsMatching = false;
                }
            } else {
                // no matching field - check productSelections for one
                const matchingProductSelection = productSelections.find(ps => ps.id == fieldId);
                if(matchingProductSelection && !values.includes(matchingProductSelection.selection)){
                    controlsMatching = false;
                }
            }
        })

        if(!controlsMatching){
            Object.entries(exception.exceptionFields).forEach(field => {
                const id = field[0];
                if(controlledFields.includes(id)) {
                    selectionsToDisable.push({field: field, side: side});
                }
            })

        }
    })

    return ({
        fields: selectionsToDisable,
        emptyCount: emptyCount
    })
}

/** @description Maps Side selections into a hash to easily find a sides current selections
 *
 * @param {array} sideSelections
 * @returns {array} A map of sideSelections
 */
export const mapSideSelectionsForExceptions = (sideSelections) => {
     return sideSelections.map((side) => {
        const sideKey = side[0];
        return ({
            [sideKey]: side[1].product_attributes.map(attr_s => {
                return ({
                    id: attr_s.id,
                    selection: attr_s.selection,
                    side: sideKey,
                    per_module: attr_s.per_module,
                    module_catalog_key: attr_s.module_catalog_key
                })
            })
        })
    });
}


/** @description Creates the skeleton for side disabled fields
 *
 * @param {array} sideAttributes
 * @returns {array} Empty Disabled fields for each side and attribute
 */
export const mapSideAttributesForDisabledFields = (sideAttributes) => {
     return sideAttributes.map((side) => {
        const sideKey = side[0];
        return ({
            [sideKey]: side[1].product_attributes.map(attribute => {
                return ({
                    id: attribute.id,
                    field: attribute.name,
                    disabledSelections: [],
                    messages: [],
                    side: sideKey
                })
            })
        })
    });
}

export const updatePatternSideModuleExceptions = () => (dispatch, getState) => {
    dispatch(disabledFieldsLoading());
    // these exceptions are at the module level, only effect a SINGLE module,
    // ie preventing Alt in a module where BP is selected. We use Module Length and
    // Module Position read only fields to allow exceptions for module lengths

    const state = getState();
    const product = state.productDataReducer;
    const sides = state.patternSelectionsReducer.sides;
    const exceptions = state.skuExceptionsReducer.exceptions
    let sideDisabledModules = {};
    sides.forEach(side => {

        const sideKey = Object.keys(side)[0];


        const perModuleSelections = side[sideKey].sidePerModuleSelections;
        const disabledModules = perModuleSelections.map(attr => ({id: attr.module_id, field: attr.position + "-" + attr.length, disabledSelections: [], messages: []  }))

        // hardcoded for now, values are passed from back end for exceptions only
        exceptions.forEach(exc => {
            if(!exc.within_module){
                return;
            }

            perModuleSelections.forEach(mod => {
                let unselectedModuleOptions;

                if(exc.inclusion){
                    unselectedModuleOptions = handleInclusiveModules(exc, mod);
                } else {
                    unselectedModuleOptions = handleExclusiveModules(exc, mod, product, true, sideKey);
                }

                // inclusive may have multiple to disable
                if(unselectedModuleOptions.length == 1 || exc.inclusion) {
                    unselectedModuleOptions.forEach(unselected => {
                        const disabledModule = disabledModules.find((df) => df.id == mod.module_id)

                        // this should always be true as we build them as we get exceptions,
                        // but we check anyways to avoid errors
                        if (disabledModule) {
                            let currentDisabled = Object.assign([], disabledModule.disabledSelections);
                            currentDisabled = currentDisabled.concat(unselected[1]);

                            disabledModules.some((df, index) => {
                                if (df.id == disabledModule.id) {
                                    disabledModules[index] = Object.assign({}, df, {
                                        disabledSelections: arrayUnique(currentDisabled.concat(df.disabledSelections)),
                                        messages: arrayUnique(df.messages.concat(exc.description))
                                    });
                                    return true
                                }
                            });
                        }
                    })
                }
            })
            sideDisabledModules = Object.assign({}, sideDisabledModules, {
                [sideKey] : disabledModules
            });
        })
    })
    dispatch(disabledSideModulesSet(sideDisabledModules));
}

export const clearDisabledFields = () => dispatch => {
    dispatch(disabledFieldsLoading());
    dispatch(disabledFieldsSet([]));
}

const reducer = disabledFieldsSlice.reducer;
export default reducer;
